import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { validateEvaluatorAccess, sendVerificationCode, verifyCode } from '@/services/verificationService';
import { Link } from 'react-router-dom';
import Logo from '../components/Logo';
import VerificationInput from '@/components/verification/VerificationInput';
import VerificationButton from '@/components/verification/VerificationButton';
import { EvaluatorVerification } from '@/types/verification';

export default function EvaluationAccess() {
  const { evaluatorEmail } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [evaluator, setEvaluator] = useState<EvaluatorVerification | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function validateAccess() {
      if (!evaluatorEmail) {
        navigate('/');
        return;
      }

      try {
        const { isValid, evaluator: evaluatorData, message } = await validateEvaluatorAccess(evaluatorEmail);

        if (!isValid || !evaluatorData) {
          toast.error(message || 'Invalid access');
          navigate('/');
          return;
        }

        setEvaluator(evaluatorData);
      } catch (error: any) {
        console.error('Error validating access:', error);
        setError(error.message || 'Failed to validate access');
        toast.error(error.message || 'Failed to validate access');
      } finally {
        setLoading(false);
      }
    }

    validateAccess();
  }, [evaluatorEmail, navigate]);

  const handleSendCode = async () => {
    if (!evaluator) return;

    try {
      setError(null);
      await sendVerificationCode(evaluator);
      toast.success('Verification code sent to your email');
    } catch (error: any) {
      console.error('Error sending code:', error);
      setError(error.message || 'Failed to send verification code');
      toast.error(error.message || 'Failed to send verification code');
      throw error; // Re-throw to be handled by VerificationButton
    }
  };

  const handleVerifyCode = async () => {
    if (!evaluator || verifying || verificationCode.length !== 6 || !acceptTerms) return;

    try {
      setVerifying(true);
      setError(null);
      const { isValid, message, token } = await verifyCode(evaluator.id, verificationCode);
      
      if (isValid && token) {
        toast.success('Email verified successfully');
        navigate(`/evaluation-form/${token}`);
      } else {
        setError(message || 'Invalid verification code');
        toast.error(message || 'Invalid verification code');
      }
    } catch (error: any) {
      console.error('Error verifying code:', error);
      setError(error.message || 'Failed to verify code');
      toast.error(error.message || 'Failed to verify code');
    } finally {
      setVerifying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex flex-col relative">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-1/2 -left-1/4 w-1/2 h-1/2 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-1/2 -right-1/4 w-1/2 h-1/2 bg-secondary/5 rounded-full blur-3xl" />
      </div>
      
      <div className="flex justify-center pt-8 pb-6 relative z-10">
        <Link to="/">
          <Logo className="h-12" />
        </Link>
      </div>

      <div className="flex-1 flex flex-col md:flex-row items-center justify-center px-4 pb-12 relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md md:w-1/2 md:mr-8 mb-8 md:mb-0"
        >
          <div className="bg-white/90 backdrop-blur-sm rounded-xl shadow-xl p-8 w-full border border-white/50">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-3">
                Welcome {evaluator?.firstName}
              </h1>
              <p className="text-gray-600 max-w-md mx-auto">
                To access your pending evaluation for {evaluator?.subjectName}, 
                let's validate your email through a confirmation code
              </p>
            </div>

            {error && (
              <div className="mb-6 bg-red-50/80 backdrop-blur-sm border border-red-200 text-red-600 px-4 py-3 rounded-lg flex items-center">
                <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="space-y-6">
              <VerificationButton
                onSendCode={handleSendCode}
                isLoading={loading}
              />

              <div className="relative flex items-center my-8">
                <div className="flex-grow border-t border-gray-200"></div>
                <span className="flex-shrink-0 mx-4 text-gray-500 bg-white/80 px-2 rounded">Or</span>
                <div className="flex-grow border-t border-gray-200"></div>
              </div>

              <div className="bg-gray-50/70 backdrop-blur-sm rounded-xl p-6 border border-gray-100">
                <div className="flex items-center text-gray-700 mb-5">
                  <Lock className="w-5 h-5 mr-2" />
                  <span>If you already have your unique code, enter it here to access the evaluation</span>
                </div>
                <VerificationInput
                  value={verificationCode}
                  onChange={setVerificationCode}
                  disabled={verifying}
                />

                {/* Terms and Privacy Policy Checkbox */}
                <div className="flex items-start mt-8">
                  <div className="flex items-center h-5">
                    <input
                      id="acceptTerms"
                      name="acceptTerms"
                      type="checkbox"
                      checked={acceptTerms}
                      onChange={(e) => setAcceptTerms(e.target.checked)}
                      className="h-5 w-5 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                  </div>
                  <div className="ml-3 pt-0.5">
                    <label htmlFor="acceptTerms" className="text-sm text-gray-600">
                      I agree to the{' '}
                      <a
                        href="https://www.iubenda.com/terms-and-conditions/37806061"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:text-primary-hover underline"
                      >
                        Terms and Conditions
                      </a>
                      {' '}and{' '}
                      <a
                        href="https://www.iubenda.com/privacy-policy/37806061"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:text-primary-hover underline"
                      >
                        Privacy Policy
                      </a>
                    </label>
                  </div>
                </div>

                <button
                  onClick={handleVerifyCode}
                  disabled={verifying || verificationCode.length !== 6 || !acceptTerms}
                  className="w-full mt-6 bg-gradient-to-r from-primary to-secondary text-dark px-6 py-3 rounded-lg hover:from-primary/90 hover:to-secondary/90 transition-all duration-300 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {verifying ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-dark border-t-transparent mr-2"></div>
                      Verifying...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Access evaluation
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
        
        {/* Hero Image - Desktop */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="hidden md:block md:w-1/2 relative"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-secondary/10 to-primary/10 rounded-3xl blur-xl opacity-30 transform -rotate-3" />
          <img 
            src="/evaluators.webp" 
            alt="InsighTalks Hero" 
            className="w-full h-auto object-contain max-h-[500px] relative z-10 drop-shadow-xl" 
          />
        </motion.div>
      </div>
      
      {/* Hero Image - Mobile */}
      <div className="md:hidden flex justify-center pb-8 relative z-10">
        <img 
          src="/evaluators.webp" 
          alt="InsighTalks Hero" 
          className="max-w-full h-auto max-h-[200px] drop-shadow-lg" 
        />
      </div>
    </div>
  );
}